import React from "react";
import { ModalWrapper } from "./ModalWindow";
import LoadingIcon from "./LoadingIcon";
import "./DiscordLinkWindow.css";

// 디스코드 공식 로고 (인라인 SVG — 외부 아이콘 패키지 의존성 없이 사용)
export function DiscordIcon({ size = 24, color = "currentColor", style }: { size?: number; color?: string; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true" style={style}>
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

export type DiscordStatus = {
  linked: boolean;
  member: boolean;
  enforce: boolean;
  discordName: string | null;
};

type DiscordLinkWindowProps = {
  handleDismiss: () => void;
  status: DiscordStatus | null;
  refresh: () => Promise<DiscordStatus | null>;
  onError: (err: string) => void;
};

const box: React.CSSProperties = { padding: "24px 28px", textAlign: "center", minWidth: 340 };
const title: React.CSSProperties = { fontSize: 18, margin: "0 0 12px", color: "#fff" };
const desc: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, color: "#bbb", margin: "0 0 20px" };
const primaryBtn: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  padding: "12px 0",
  border: "none",
  borderRadius: 6,
  background: "#5865F2",
  color: "#fff",
  fontSize: 15,
  cursor: "pointer",
  marginBottom: 10,
};
const subBtn: React.CSSProperties = { ...primaryBtn, background: "#3a3d44" };

export default function DiscordLinkWindow({ handleDismiss, status, refresh, onError }: DiscordLinkWindowProps) {
  const [busy, setBusy] = React.useState(false);

  // "디스코드 연동하기" — 서버에서 OAuth URL 받아 기본 브라우저로 열기
  async function startLink() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/discord/authUrl`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.url) {
        onError(data.msg || "연동 URL을 가져오지 못했습니다. 다시 시도해주세요.");
        return;
      }
      window.ipcRenderer.send("openExternal", data.url);
    } catch (e) {
      onError("연동 요청 중 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  }

  // "연동 확인" — 상태 새로고침
  async function recheck() {
    if (busy) return;
    setBusy(true);
    try {
      const next = await refresh();
      if (next && next.linked && next.member) {
        onError(""); // 성공 시 에러 메시지 정리
      }
    } finally {
      setBusy(false);
    }
  }

  let body: React.ReactNode;

  if (status && status.linked && status.member) {
    body = (
      <>
        <h2 style={title}>디스코드 연동 완료</h2>
        <p style={desc}>
          {status.discordName ? `${status.discordName} 계정과 ` : ""}연동되어 있으며 서버에 참여 중입니다.
          <br />
          게임을 정상적으로 시작할 수 있습니다.
        </p>
        <button style={primaryBtn} onClick={handleDismiss} disabled={busy}>
          닫기
        </button>
      </>
    );
  } else if (status && status.linked && !status.member) {
    body = (
      <>
        <h2 style={title}>디스코드 서버 참여·인증 필요</h2>
        <p style={desc}>
          연동된 디스코드 계정이 서버에 없거나 필요한 인증이 없어 게임을 시작할 수 없습니다.
          <br />
          디스코드 서버 참여 및 인증을 완료한 뒤 아래 {'"연동 확인"'}을 눌러주세요.
        </p>
        <button style={subBtn} onClick={recheck} disabled={busy}>
          연동 확인
        </button>
      </>
    );
  } else {
    body = (
      <>
        <h2 style={title}>디스코드 연동이 필요합니다</h2>
        <p style={desc}>
          게임을 시작하려면 디스코드 계정 연동이 필요합니다.
          <br />
          {'"디스코드 연동하기"'}를 눌러 브라우저에서 인증한 뒤,
          <br />
          다시 돌아와 {'"연동 확인"'}을 눌러주세요.
          <br />
          <span style={{ fontSize: 12, color: "#888" }}>(디스코드 계정 1개당 테라 계정 최대 3개까지 연동 가능)</span>
        </p>
        <button style={primaryBtn} onClick={startLink} disabled={busy}>
          <DiscordIcon size={18} color="#fff" />
          디스코드 연동하기
        </button>
        <button style={subBtn} onClick={recheck} disabled={busy}>
          연동 확인
        </button>
      </>
    );
  }

  return (
    <ModalWrapper handleDismiss={handleDismiss} className="Discord-Link-Window">
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ padding: "20px 0 4px" }}>
          <DiscordIcon size={46} color="#5865F2" />
        </div>
        <div style={box}>{body}</div>
      </div>
      {busy && <LoadingIcon dim />}
    </ModalWrapper>
  );
}
